import type { TileOf } from '@casa/model';
import { formatNumber } from '@casa/util';
import type { Rect } from '../../elements/flex_label.ts';
import { transformValue } from '../../values/transform.ts';

/**
 * The legacy `table` tile (`TableTile.cs`, `TableComponent.cs`, `TextComponent.cs`): the column
 * labels, then a row of cells for each row of the options, on a grid 10 apart. Columns are as
 * wide as their weight; all rows, the labels' included, are as high as each other. All text has
 * one size, the largest at which every text fits its cell (table-tile.md TT-1), in white unless
 * the cell has a colour; the labels may be bold (TT-2), and lines may divide the rows and the
 * columns (TT-3). A number is transformed and formatted; with no number it shows "-".
 */

export type TableTile = TileOf<'table'>;
export type TableLines = TableTile['options']['rowLines'];

/** The legacy grid's `RowSpacing` and `ColumnSpacing`. */
export const TABLE_SPACING = 10;
/** The legacy `ValueOptions.Color` default. */
export const TABLE_TEXT_COLOR = '#ffffff';
/** How strong a divider line is, in parts of the text colour (TT-4). */
export const TABLE_LINE_ALPHA = 1 / 3;

export interface TableCellData {
    value: number | null;
}

export interface TableCellContent {
    text: string;
    color: string;
}

/** The cells' sources in reading order (fixed text has none), for `useReadingsList`. */
export function tableSources(tile: TableTile) : (string | undefined)[] {
    return tile.options.rows.flatMap((row) => row.map((cell) => (typeof cell === 'string' ? undefined : cell.source)));
}

/** Every cell's text and colour, row by row, the column labels first; `data` follows `tableSources`. */
export function tableTileContent(tile: TableTile, data: readonly TableCellData[], locale: string | undefined, textColor = TABLE_TEXT_COLOR) : TableCellContent[][] {
    const header = tile.options.columns.map((column) => ({ text: column.label, color: textColor }));
    let index = 0;
    const rows = tile.options.rows.map((row) =>
        row.map((cell) : TableCellContent => {
            if (typeof cell === 'string') {
                return { text: cell, color: textColor };
            }

            const number = transformValue((data[index++] ?? { value: null }).value, cell.transform);

            return { text: number === null ? '-' : formatNumber(number, cell.numberFormat, locale), color: cell.color ?? textColor };
        }),
    );

    return [header, ...rows];
}

/** Where each cell goes in a content area of `width` × `height`: `rows` rows of the columns' weights. */
export function tableCellRects(weights: readonly number[], rows: number, width: number, height: number) : Rect[][] {
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    const columnSpace = width - (weights.length - 1) * TABLE_SPACING;
    const rowHeight = (height - (rows - 1) * TABLE_SPACING) / rows;
    const columns: { left: number; width: number }[] = [];
    let left = 0;
    for (const weight of weights) {
        const columnWidth = (columnSpace * weight) / total;
        columns.push({ left, width: columnWidth });
        left += columnWidth + TABLE_SPACING;
    }

    return Array.from({ length: rows }, (_, row) => columns.map((column) => ({ left: column.left, top: row * (rowHeight + TABLE_SPACING), width: column.width, height: rowHeight })));
}

/**
 * The one font size of every text (TT-1): the smallest of the sizes at which each fits its cell,
 * `fit` giving one text's in its box, in bold for the labels with `boldFirstRow`. Empty cells do
 * not count; undefined where there is no text.
 */
export function tableTextSize(content: readonly (readonly TableCellContent[])[], rects: readonly (readonly Rect[])[], boldFirstRow: boolean, fit: (text: string, box: Rect, isBold: boolean) => number) : number | undefined {
    let size: number | undefined;

    for (const [row, cells] of content.entries()) {
        for (const [column, cell] of cells.entries()) {
            const box = rects[row]?.[column];
            if (box && cell.text !== '') {
                const fitting = fit(cell.text, box, boldFirstRow && row === 0);
                size = size === undefined ? fitting : Math.min(size, fitting);
            }
        }
    }

    return size;
}

/** A divider line: across the table at `y`, or down it at `x` (TT-3). */
export type TableLine = { y: number } | { x: number };

/** Where the divider lines go, in the middle of the gaps after the first row or column, or after each (TT-4). */
export function tableLines(rects: readonly (readonly Rect[])[], rowLines: TableLines, columnLines: TableLines) : TableLine[] {
    const firstRow = rects[0] ?? [];
    const lines: TableLine[] = [];
    const rowsWithLines = rowLines === 'all' ? rects.length - 1 : rowLines === 'first' ? Math.min(1, rects.length - 1) : 0;
    const columnsWithLines = columnLines === 'all' ? firstRow.length - 1 : columnLines === 'first' ? Math.min(1, firstRow.length - 1) : 0;

    for (let row = 0; row < rowsWithLines; row++) {
        const cell = rects[row]![0]!;
        lines.push({ y: cell.top + cell.height + TABLE_SPACING / 2 });
    }

    for (let column = 0; column < columnsWithLines; column++) {
        const cell = firstRow[column]!;
        lines.push({ x: cell.left + cell.width + TABLE_SPACING / 2 });
    }

    return lines;
}
